import React from "react";

import {
  FaCertificate,
  FaEuroSign,
  FaHandHoldingUsd,
  FaMoneyBillWave,
  FaPercentage,
  FaTicketAlt,
} from "react-icons/fa";

const CURRENCY = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatCurrency(value) {
  return CURRENCY.format(Number(value || 0));
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("fr-FR");
}

function KpiCard({
  icon,
  label,
  value,
  helper,
  accent = "indigo",
  loading = false,
}) {
  const accents = {
    indigo:
      "from-indigo-600/15 to-sky-500/10 text-indigo-700 dark:text-indigo-300 ring-indigo-200/70 dark:ring-indigo-500/20",
    emerald:
      "from-emerald-600/15 to-lime-500/10 text-emerald-700 dark:text-emerald-300 ring-emerald-200/70 dark:ring-emerald-500/20",
    amber:
      "from-amber-500/15 to-orange-500/10 text-amber-700 dark:text-amber-300 ring-amber-200/70 dark:ring-amber-500/20",
    rose:
      "from-rose-600/15 to-pink-500/10 text-rose-700 dark:text-rose-300 ring-rose-200/70 dark:ring-rose-500/20",
    violet:
      "from-violet-600/15 to-fuchsia-500/10 text-violet-700 dark:text-violet-300 ring-violet-200/70 dark:ring-violet-500/20",
    slate:
      "from-slate-600/15 to-slate-400/10 text-slate-700 dark:text-slate-300 ring-slate-200/70 dark:ring-slate-500/20",
  };

  return (
    <div
      className={`rounded-3xl bg-gradient-to-br ${accents[accent]} p-4 shadow-sm ring-1`}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/80 text-lg dark:bg-slate-900/50">
          {icon}
        </div>

        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.18em] opacity-70">
            {label}
          </p>

          <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
            {loading ? "…" : value}
          </p>

          {helper ? (
            <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">
              {helper}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function ComptaKpiCards({ stats, loading = false }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
      <KpiCard
        icon={<FaCertificate />}
        label="Licences"
        value={formatCurrency(stats.licences)}
        helper="Total licences / assurances"
        accent="indigo"
        loading={loading}
      />

      <KpiCard
        icon={<FaEuroSign />}
        label="Cotisations - réductions"
        value={formatCurrency(stats.cotisationsNettes)}
        helper="Cotisations nettes après réductions"
        accent="emerald"
        loading={loading}
      />

      <KpiCard
        icon={<FaPercentage />}
        label="Réductions"
        value={formatCurrency(stats.reductions)}
        helper="Foyer, saison et Pass’Sport"
        accent="rose"
        loading={loading}
      />

      <KpiCard
        icon={<FaMoneyBillWave />}
        label="Règlements"
        value={formatCurrency(stats.totalReglements)}
        helper={`${formatNumber(stats.nombreReglements)} ligne(s)`}
        accent="amber"
        loading={loading}
      />

      <KpiCard
        icon={<FaHandHoldingUsd />}
        label="Pass’Sport"
        value={formatCurrency(stats.passSport)}
        helper="Montant total Pass’Sport"
        accent="violet"
        loading={loading}
      />

      <KpiCard
        icon={<FaTicketAlt />}
        label="Nb Pass’Sport"
        value={formatNumber(stats.nombrePassSport)}
        helper="Base estimée de 50 €"
        accent="slate"
        loading={loading}
      />
    </div>
  );
}